/*
 * MAGOURDE : fonctions ajoutées au thème Horizon
 * (« Fréquemment achetés ensemble »).
 */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ------------------------------------------------------------------ outils */
function formatMoney(cents, format) {
  const fmt = format || '{{amount_with_comma_separator}} €';
  const value = Number(cents) / 100;
  const group = (num, decimals, thousands, decimal) => {
    const parts = num.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_space_separator':
        return group(value, 2, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

function moneyFormatFrom(el) {
  const tpl = el.querySelector('template[data-money-format]');
  return tpl ? tpl.content.textContent.trim() || tpl.innerHTML.replace(/<[^>]*>/g, '').trim() : undefined;
}

/* ---------------------------------------- Fréquemment achetés ensemble */
class MgBundle extends HTMLElement {
  connectedCallback() {
    if (this.querySelector('[data-mg-bundle-content]')) {
      this.init();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    io.observe(this);
    // sécurité : chargement garanti même si l'observateur ne se déclenche pas
    setTimeout(() => {
      io.disconnect();
      this.load();
    }, 4000);
  }

  async fetchContent(url) {
    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('mg-bundle [data-mg-bundle-content]');
    if (!content) return null;
    // au moins un produit proposé
    if (content.querySelectorAll('input[data-variant-id]').length < 1) return null;
    return content;
  }

  async load() {
    // un seul chargement par bloc
    if (this.loadStarted) return;
    this.loadStarted = true;
    // éléments rendus sur la fiche produit (produit actuel, variantes, format monétaire)
    const currentTpl = this.querySelector('template[data-mg-current]');
    const variantsEl = this.querySelector('script[data-mg-variants]');
    const moneyTpl = this.querySelector('template[data-money-format]');
    try {
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    } catch (e) {
      this.variants = [];
    }
    try {
      let found = await this.fetchContent(this.dataset.url);
      if (!found && this.dataset.fallbackUrl && this.dataset.fallbackUrl !== this.dataset.url) {
        found = await this.fetchContent(this.dataset.fallbackUrl);
      }
      if (!found) {
        this.closest('.shopify-section')?.classList.add('mg-hidden');
        return;
      }
      this.replaceChildren(found);
      if (moneyTpl) this.append(moneyTpl);
      if (currentTpl) {
        const list = found.querySelector('.mg-bundle__items');
        list?.prepend(currentTpl.content.cloneNode(true));
      }
      this.init();
    } catch (e) {
      console.warn('[MAGOURDE] bundle', e);
    }
  }

  init() {
    this.format = moneyFormatFrom(this);
    this.variants = this.variants || [];
    this.addEventListener('change', () => this.updateTotal());
    // la variante choisie dans la fiche produit est reprise dans le lot :
    // on attend que le thème ait fini de charger la nouvelle variante (événement productSelect)
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        document.addEventListener(StandardEvents.productSelect, (event) => {
          if (this.contains(event.target)) return;
          this.pending = true;
          this.updateTotal();
          Promise.resolve(event.promise)
            .catch(() => null)
            .then(() => {
              setTimeout(() => {
                this.pending = false;
                this.syncCurrent();
              }, 0);
            });
        });
      })
      .catch(() => {});
    // sécurité si l'événement n'est pas disponible
    document.addEventListener('change', (event) => {
      if (this.contains(event.target)) return;
      setTimeout(() => this.syncCurrent(), 700);
      setTimeout(() => this.syncCurrent(), 2000);
    });
    this.syncCurrent();
    this.querySelector('[data-mg-bundle-add]')?.addEventListener('click', () => this.addToCart());
    this.updateTotal();
  }

  currentVariantId() {
    const input = document.querySelector(
      'product-form-component form input[name="id"], form[action*="/cart/add"]:not(mg-bundle form) input[name="id"]'
    );
    return input ? Number(input.value) : null;
  }

  syncCurrent() {
    const checkbox = this.querySelector('input[data-current]');
    if (!checkbox) return;
    const id = this.currentVariantId();
    const variant = this.variants.find((v) => v.id === id);
    if (!variant) {
      this.updateTotal();
      return;
    }
    checkbox.dataset.variantId = String(variant.id);
    checkbox.dataset.price = String(variant.price);
    checkbox.dataset.compare = String(variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price);
    checkbox.disabled = !variant.available;
    if (!variant.available) checkbox.checked = false;
    const vt = this.querySelector('[data-mg-current-variant]');
    if (vt) vt.textContent = variant.title;
    const pr = this.querySelector('[data-mg-current-price]');
    if (pr) {
      pr.textContent = formatMoney(variant.price, this.format);
      if (variant.compare_at_price > variant.price) {
        const s = document.createElement('s');
        s.textContent = formatMoney(variant.compare_at_price, this.format);
        pr.append(' ', s);
      }
    }
    this.updateTotal();
  }

  selected() {
    return [...this.querySelectorAll('input[data-variant-id]:checked')];
  }

  updateTotal() {
    const items = this.selected();
    const total = items.reduce((s, i) => s + Number(i.dataset.price), 0);
    const compare = items.reduce((s, i) => s + Number(i.dataset.compare), 0);
    const totalEl = this.querySelector('[data-mg-bundle-total]');
    const compareEl = this.querySelector('[data-mg-bundle-compare]');
    if (totalEl) totalEl.textContent = formatMoney(total, this.format);
    if (compareEl) compareEl.textContent = compare > total ? formatMoney(compare, this.format) : '';
    const btn = this.querySelector('[data-mg-bundle-add]');
    if (btn) btn.disabled = items.length === 0 || Boolean(this.pending);
  }

  async addToCart() {
    const btn = this.querySelector('[data-mg-bundle-add]');
    const errorEl = this.querySelector('[data-mg-bundle-error]');
    if (this.pending) return;
    this.syncCurrent();
    const items = this.selected().map((i) => ({ id: Number(i.dataset.variantId), quantity: 1 }));
    if (!items.length) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    let events = null;
    let deferred = null;
    try {
      events = await import('@shopify/events');
      deferred = events.CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
          promise: deferred.promise,
        })
      );
    } catch (e) {
      events = null;
    }

    try {
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Erreur');

      const cart = await fetch(`${ROOT}cart.js`).then((r) => r.json());
      if (events && deferred) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'mg-bundle', sections: data.sections, didError: false },
        });
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
        else if (!drawer) window.location.href = `${ROOT}cart`;
      } else {
        window.location.href = `${ROOT}cart`;
      }
    } catch (e) {
      if (events && deferred) {
        const cart = await fetch(`${ROOT}cart.js`).then((r) => r.json()).catch(() => null);
        if (cart) {
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'mg-bundle', didError: true },
          });
        }
      }
      if (errorEl) {
        errorEl.textContent = e.message;
        errorEl.hidden = false;
      }
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('mg-bundle')) customElements.define('mg-bundle', MgBundle);
